import { FC, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { AgentHarnessPanel, PanelDragProps } from '@/components/admin/harness/AgentHarnessPanel'
import { useAgentHarness } from '@/components/admin/harness/AgentHarnessContext'
import { cn } from '@/lib/utils'

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v))

const MIN_W = 560
const MIN_H = 400
const GEOM_KEY = 'jingque-admin:agent-panel-geom'
const BALL_KEY = 'jingque-admin:agent-ball-pos'
const BALL_SIZE = 56 // size-14

type BallPos = { right: number; bottom: number }

const clampBallPos = (p: BallPos): BallPos => ({
  right: clamp(p.right, 8, window.innerWidth - BALL_SIZE - 8),
  bottom: clamp(p.bottom, 8, window.innerHeight - BALL_SIZE - 8),
})

const loadBallPos = (): BallPos => {
  try {
    const raw = localStorage.getItem(BALL_KEY)
    if (raw) {
      const p = JSON.parse(raw)
      if ([p.right, p.bottom].every(Number.isFinite)) return clampBallPos(p)
    }
  } catch {
    /* corrupted JSON — fall back to default */
  }
  return { right: 24, bottom: 24 }
}

type Geom = { w: number; h: number; dx: number; dy: number }

const defaultGeom = (): Geom => ({
  w: Math.min(1200, window.innerWidth - 48),
  h: Math.min(window.innerHeight * 0.7, 700),
  dx: 0,
  dy: 0,
})

// 四值统一收敛到视口内;dx/dy 复用现有拖动 clamp 公式,保证拖动边界永远对得上当前尺寸
const clampGeom = (g: Geom): Geom => {
  const vw = window.innerWidth
  const vh = window.innerHeight
  const w = clamp(g.w, MIN_W, vw - 16)
  const h = clamp(g.h, MIN_H, vh - 16)
  const dx = clamp(g.dx, 24 - (vw - 160), 24 + (w - 120))
  const dy = clamp(g.dy, 96 - (vh - 80), 96 - 8)
  return { w, h, dx, dy }
}

const loadGeom = (): Geom => {
  try {
    const raw = localStorage.getItem(GEOM_KEY)
    if (raw) {
      const p = JSON.parse(raw)
      if ([p.w, p.h, p.dx, p.dy].every(Number.isFinite)) return clampGeom(p)
    }
  } catch {
    /* corrupted JSON — fall back to default */
  }
  return defaultGeom()
}

/**
 * 全局 Agent 悬浮球:右下角圆球 ⇄ 弹窗式面板。
 * - /agent-harness 路由上整体隐藏(整页已是全屏,避免冗余)
 * - 收起时面板 visibility:hidden 保持挂载(流式不打断、DOM 状态保留);状态在 Context,重开无损
 * - 头部空白处可拖动窗口(pointer capture;right/bottom 定位,与开合 transform 动画不冲突;拖动中关 transition)
 * - 右下角 grip 可缩放:左上角钉死、右下角跟手;尺寸+位置持久化到 localStorage
 * - z-index:面板 1020 / 球 1021 — 高于 antd Drawer/Modal(1000),低于 Select 下拉(1050)/Popover(1030),面板内弹层正常
 */
export const AgentFloatWidget: FC = () => {
  const { pathname } = useLocation()
  const { anyRunning } = useAgentHarness()
  const [open, setOpen] = useState(false)
  const [rendered, setRendered] = useState(false) // 首次打开后才挂载面板(延迟初始化成本)
  const [geom, setGeom] = useState<Geom>(loadGeom)
  const [dragging, setDragging] = useState(false)
  const [resizing, setResizing] = useState(false)
  const dragRef = useRef<{ sx: number; sy: number; bx: number; by: number } | null>(null)
  const resizeRef = useRef<{ sx: number; sy: number; w: number; h: number; dx: number; dy: number } | null>(null)
  // 悬浮球拖拽:位移 >4px 视为拖动(抑制点击),位置持久化;挡内容时可拖走
  const [ballPos, setBallPos] = useState<BallPos>(loadBallPos)
  const [ballDragging, setBallDragging] = useState(false)
  const ballDragRef = useRef<{ sx: number; sy: number; br: number; bb: number; moved: boolean } | null>(null)

  // Esc 快速收起
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  useEffect(() => {
    if (open) setRendered(true)
  }, [open])

  // 视口变化时收敛(换屏/缩浏览器),保证位置+尺寸始终合法
  useEffect(() => {
    const onWinResize = () => {
      setGeom(g => clampGeom(g))
      setBallPos(p => clampBallPos(p))
    }
    window.addEventListener('resize', onWinResize)
    return () => window.removeEventListener('resize', onWinResize)
  }, [])

  // 持久化:仅非交互态写入,避免 pointermove 高频写盘 + 规避 StrictMode 下 updater 副作用
  useEffect(() => {
    if (dragging || resizing) return
    try {
      localStorage.setItem(GEOM_KEY, JSON.stringify(geom))
    } catch {
      /* quota / disabled — silent */
    }
  }, [geom, dragging, resizing])

  const dragProps: PanelDragProps = {
    onPointerDown: e => {
      // 头部内的按钮/输入等交互元素不触发拖动
      if ((e.target as HTMLElement).closest('button, input, a, .ant-select, .ant-popover')) return
      dragRef.current = { sx: e.clientX, sy: e.clientY, bx: geom.dx, by: geom.dy }
      ;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
      setDragging(true)
    },
    onPointerMove: e => {
      const d = dragRef.current
      if (!d) return
      const vw = window.innerWidth
      // effectiveRight = 24 - dx,限制窗口不拖出视口(至少露出 120px;右侧最多贴到剩 160px)
      const dx = clamp(d.bx + (e.clientX - d.sx), 24 - (vw - 160), 24 + (geom.w - 120))
      // effectiveBottom = 96 - dy,下限 8,上限保留 80px 可视
      const dy = clamp(d.by + (e.clientY - d.sy), 96 - (window.innerHeight - 80), 96 - 8)
      setGeom(g => ({ ...g, dx, dy }))
    },
    onPointerUp: () => {
      dragRef.current = null
      setDragging(false)
    },
    onPointerCancel: () => {
      dragRef.current = null
      setDragging(false)
    },
  }

  // 缩放把手:右下角 grip,左上角钉死、右下角跟手(尺寸+偏移同步 net 增量)
  const gripProps = {
    onPointerDown: (e: React.PointerEvent) => {
      e.preventDefault()
      e.stopPropagation()
      resizeRef.current = {
        sx: e.clientX,
        sy: e.clientY,
        w: geom.w,
        h: geom.h,
        dx: geom.dx,
        dy: geom.dy,
      }
      ;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
      setResizing(true)
    },
    onPointerMove: (e: React.PointerEvent) => {
      const r = resizeRef.current
      if (!r) return
      const vw = window.innerWidth
      const vh = window.innerHeight
      // 右缘 ≤ vw-8 → dw ≤ 16 - dx₀;总宽 ≤ vw-16。两者取严
      const dw = clamp(e.clientX - r.sx, MIN_W - r.w, Math.min(16 - r.dx, vw - 16 - r.w))
      // 下缘 ≤ vh-8 → dh ≤ 88 - dy₀;总高 ≤ vh-16
      const dh = clamp(e.clientY - r.sy, MIN_H - r.h, Math.min(88 - r.dy, vh - 16 - r.h))
      setGeom(() => ({ w: r.w + dw, h: r.h + dh, dx: r.dx + dw, dy: r.dy + dh }))
    },
    onPointerUp: () => {
      resizeRef.current = null
      setResizing(false)
    },
    onPointerCancel: () => {
      resizeRef.current = null
      setResizing(false)
    },
  }

  if (pathname === '/agent-harness' || pathname.startsWith('/agent-harness/')) return null

  return (
    <>
      {/* 弹窗面板:保持挂载,收起仅隐藏(动画:缩回右下角球) */}
      {rendered && (
        <div
          className={cn(
            'fixed transition-all duration-200 ease-out',
            (dragging || resizing) && 'transition-none',
            open
              ? 'pointer-events-auto translate-y-0 scale-100 opacity-100'
              : 'pointer-events-none translate-y-3 scale-95 opacity-0 invisible',
          )}
          style={{
            right: 24 - geom.dx,
            bottom: 96 - geom.dy,
            width: geom.w,
            height: geom.h,
            zIndex: 1020,
            transformOrigin: 'bottom right',
          }}
          aria-hidden={!open}
        >
          <AgentHarnessPanel
            variant="popup"
            onCollapse={() => setOpen(false)}
            dragProps={dragProps}
          />
          {/* 右下角缩放把手(纯 UI,Panel 零改动):三斜纹经典辨识 */}
          <div
            className="absolute bottom-0 right-0 z-10 flex size-5 cursor-nwse-resize touch-none select-none items-end justify-end p-0.5 text-muted-foreground/40 transition-colors hover:text-muted-foreground"
            title="拖动调整大小"
            {...gripProps}
          >
            <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden>
              <path d="M9 2 L2 9" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
              <path d="M9 5 L5 9" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
              <path d="M9 8 L8 9" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
            </svg>
          </div>
        </div>
      )}

      {/* 悬浮球:菁鹊 logo;可拖拽移位(挡内容时拖走,位置持久化);闲时半透 hover 恢复;running 绿色脉冲点 */}
      <button
        type="button"
        aria-label={open ? '收起 Agent' : '打开 Agent'}
        title={open ? '收起 Agent(可拖动换位)' : '打开 Agent(可拖动换位)'}
        onPointerDown={e => {
          ballDragRef.current = { sx: e.clientX, sy: e.clientY, br: ballPos.right, bb: ballPos.bottom, moved: false }
          ;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
        }}
        onPointerMove={e => {
          const d = ballDragRef.current
          if (!d) return
          const dx = e.clientX - d.sx
          const dy = e.clientY - d.sy
          if (!d.moved && Math.hypot(dx, dy) <= 4) return
          d.moved = true
          setBallDragging(true)
          setBallPos(clampBallPos({ right: d.br - dx, bottom: d.bb - dy }))
        }}
        onPointerUp={() => {
          const d = ballDragRef.current
          ballDragRef.current = null
          setBallDragging(false)
          if (!d) return
          if (d.moved) {
            // 拖动结束:持久化位置
            try {
              localStorage.setItem(BALL_KEY, JSON.stringify(ballPos))
            } catch {
              /* quota — silent */
            }
          } else {
            setOpen(v => !v)
          }
        }}
        onPointerCancel={() => {
          ballDragRef.current = null
          setBallDragging(false)
        }}
        className={cn(
          'fixed flex size-14 items-center justify-center overflow-hidden rounded-full',
          'bg-background shadow-lg ring-1 ring-border transition-opacity',
          ballDragging ? 'cursor-grabbing' : 'cursor-pointer transition-transform hover:scale-105',
          open
            ? 'scale-90 opacity-90'
            : 'opacity-60 hover:opacity-100',
        )}
        style={{ zIndex: 1021, right: ballPos.right, bottom: ballPos.bottom }}
      >
        <img
          src="https://middleiman-public.oss-cn-hangzhou.aliyuncs.com/jingque_avatar.webp"
          alt="菁鹊"
          draggable={false}
          className="size-full rounded-full object-cover"
        />
        {anyRunning && (
          <span className="absolute right-0.5 top-0.5 flex size-3">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex size-3 rounded-full border-2 border-white bg-emerald-500" />
          </span>
        )}
      </button>
    </>
  )
}